import * as t from 'io-ts';
import { isUndefined, isArray } from 'lodash-es';
import type { PartialBy, Signal } from '@bayer-int/portletce-shared';

import type {
    SettingFormInputDefinition,
    InputDefinition,
    AnyInteractiveTitleSettingDefinition,
    InteractiveTitleSettingDefinition,
    SelectClearableDefinition,
    InteractiveTitleSelectClearableDefinition,
    SelectDefinition,
    InteractiveTitleSelectDefinition,
    SelectNumberDefinition,
    InteractiveTitleSelectNumberDefinition,
    SelectNumberClearableDefinition,
    InteractiveTitleSelectNumberClearableDefinition,
    CheckBoxDefinition,
    MultiSelectDefinition,
    InteractiveTitleMultiSelectDefinition,
    RichMultiSelectDefinition,
    NumberInputDefinition,
    InteractiveTitleNumberInputDefinition,
} from 'header/types';
import type {
    FilterData,
    FilterIdToSignalMapping,
    PortletQuery,
    Specification,
    View,
} from 'portlet/models';

import type { Settings, SettingValueMap, SettingValueMapDiff } from '../index';

/**
 * Interface which must be implemented by each portlet setting.
 *
 * When defining settings use the `define` helper for hints and intellisense
 */
export interface CommonSettingDefinition<K extends string, T extends SettingFormInputDefinition> {
    /** Unique setting identifier, also serves purpose of the signal name when passed to aclib */
    key: K;
    /** User friendly label for setting key identifier */
    label: string;
    /** Form definition */
    definition: T;
    /** Tags which will be replaced with setting's value in portlet title */
    replacementTags: (definition: T) => Record<string, string>;
    /**
     * Tags which will be replaced with simple inputs in portlet title,
     * which value change should trigger a setting value change.
     * One setting can define multiple interactive tags which change the value, that is
     * why each of them needs to `format` the tag value back to the setting value.
     */
    interactiveTags: (definition: T) => Record<string, AnyInteractiveTitleSettingDefinition>;
    /**
     * Returns value based on the `settings` field
     * defined on specification or view and the specification.
     * If returns `undefined`, the setting will not be created.
     *
     * In order to support plain (just the value) and object notation
     * (object with value field and extra properties), use `createSpecSettingsInterface`
     */
    fromSetting: (
        settings: Partial<Settings>,
        spec: Specification,
        view: View | null,
    ) => T['value'] | undefined;
    /** Creates form definition with custom options */
    createDefinitionWithOpts: (opts?: Partial<T>) => T;
    /**
     * Creates form definition based on the `settings` field
     * defined on specification or view, and the specification
     * If returns `undefined`, the form will not be created.
     */
    createDefinitionFromSpec: (
        settings: Partial<Settings>,
        spec: Specification,
        view: View | null,
    ) => T | undefined;
    /**
     * Optional function that allows for applying dependencies across multiple settings.
     * For example, value of setting A has changed, and therefore value setting of setting B
     * should be changed automatically. In such scenario, setting B should implement this function
     * and react for changes of A.
     *
     * Returning `undefined` means no change should be applied to the setting.
     * Returning { value: undefined } means that the setting should be **unset**.
     * Returning any other { value: ... } means that the new value should be set.
     */
    applySettingDependency?: (
        _: UpdateProps<K, T>,
    ) => { value: T['value'] | undefined } | undefined;
    /**
     * Allows for defining how the setting should be serialized to a vega signal.
     * The definition argument is expected to contain the most recent setting value.
     * By default it will be a signal with `key` being the `name` and `value` being current setting value.
     */
    toSignals: (
        definition: T,
        props: {
            mergedSettings: Partial<SettingValueMap>;
            currentViewSignals: Signal[];
            filters: FilterData[];
            queryMetadata: PortletQuery | null;
            filterToSignalName: FilterIdToSignalMapping;
        },
    ) => Signal[];
}

export const define = <K extends string, T extends SettingFormInputDefinition>(
    d: PartialBy<
        CommonSettingDefinition<K, T>,
        'replacementTags' | 'interactiveTags' | 'toSignals'
    >,
): CommonSettingDefinition<K, T> => ({
    replacementTags: defaultCreateReplacementTags,
    interactiveTags: defaultCreateInteractiveTags,
    toSignals: defaultToSignals,
    ...d,
});

type UpdateProps<K extends string, T extends SettingFormInputDefinition> = CommonSettingDefinition<
    K,
    T
> & {
    specification: Specification;
    view: View | null;
    currentDefinition: T;
    diff: SettingValueMapDiff;
    settings: Partial<SettingValueMap>;
    previousSettings: Partial<SettingValueMap>;
    filters: FilterData[];
    listenToSignalMapping: FilterIdToSignalMapping;
};

/**
 * In order to support specifying settings values at spec level either as plain values,
 * or objects with `value` property (and/or other extra metadata), this interface
 * encapsulates creation of a codec that represents both of syntaxes and defines extra functions
 * that simplify accessing the values.
 */
export interface SpecSettings<
    C extends t.Mixed,
    D extends InputDefinition<unknown>,
    E extends t.Mixed = t.PartialC<Record<never, string>>,
> {
    /** codec representing the setting input that may be present on the spec */
    input: t.Type<SpecSettingInput<t.TypeOf<C>> & t.TypeOf<E>>;
    /** returns true if setting is specified in object notation */
    isObjectNotation: (
        value?: SpecSettingInput<t.TypeOf<C>> & t.TypeOf<E>,
    ) => value is SpecSettingObjectNotation<t.TypeOf<C>> & t.TypeOf<E>;
    /**
     * detects if setting value is defined as plain value or `{ value: T }`,
     * and unwraps it to T | undefined
     */
    extractValue: (value?: SpecSettingInput<t.TypeOf<C>> & t.TypeOf<E>) => t.TypeOf<C> | undefined;
    /**
     * if the setting is in object notation and provides extra properties,
     * they will be merged with default setting definition
     */
    createDefinitionWithSettings: (
        value?: SpecSettingInput<t.TypeOf<C>> & t.TypeOf<E>,
    ) => D | undefined;
}

type SpecSettingObjectNotation<T> = {
    value: T;
} & Partial<InputDefinition<unknown>>;

type SpecSettingInput<T> = T | SpecSettingObjectNotation<T>;

/**
 * Creates instance of `SpecSettings` for given setting. Overloading required
 * for optional parameter with extra properties that can be defined on the
 * object notation value.
 */
export function createSpecSettingsInterface<C extends t.Mixed, D extends InputDefinition<unknown>>(
    definition: D,
    plainCodec: C,
): SpecSettings<C, D>;
export function createSpecSettingsInterface<
    C extends t.Mixed,
    D extends InputDefinition<unknown>,
    E extends t.Mixed,
>(definition: D, plainCodec: C, extraPropertiesCodec: E): SpecSettings<C, D, E>;
export function createSpecSettingsInterface<
    C extends t.Mixed,
    D extends InputDefinition<unknown>,
    E extends t.Mixed,
>(
    definition: D,
    plainCodec: C,
    extraPropertiesCodec: E = t.partial({}) as unknown as E,
): SpecSettings<C, D, E> {
    const supportedCommonInputDefinitionPropertiesCodec: t.Type<Partial<InputDefinition<unknown>>> =
        t.partial({
            scope: t.union([t.literal('user'), t.literal('designer'), t.literal('none')]),
        });

    const objectCodec = t.intersection(
        [
            t.type({ value: plainCodec }),
            supportedCommonInputDefinitionPropertiesCodec,
            extraPropertiesCodec,
        ],
        'SettingInputObjectNotation',
    );

    const input = t.union(
        [plainCodec, objectCodec],
        `SpecSetting<${definition.key}, ${plainCodec.name}, ${extraPropertiesCodec.name}>`,
    );

    return {
        input,
        extractValue: value =>
            objectCodec.is(value) ? value.value : plainCodec.is(value) ? value : undefined,

        createDefinitionWithSettings: value =>
            isUndefined(value)
                ? undefined
                : { ...definition, ...(objectCodec.is(value) && { scope: value.scope }) },

        isObjectNotation: (value): value is t.TypeOf<typeof objectCodec> => objectCodec.is(value),
    };
}

export function defaultCreateReplacementTags<T extends SettingFormInputDefinition>(
    definition: T,
): Record<string, string> {
    const value = isArray(definition.value)
        ? `[${definition.value.join(', ')}]`
        : definition.value?.toString() || '';

    return { [definition.key]: value };
}

/**
 * Generates interactive tags for most common types of settings.
 */
export function defaultCreateInteractiveTags<T extends SettingFormInputDefinition>(
    definition: T,
): Record<string, AnyInteractiveTitleSettingDefinition> {
    switch (definition.type) {
        case 'Select': {
            const tag: InteractiveTitleSettingDefinition<
                SelectDefinition,
                InteractiveTitleSelectDefinition
            > = {
                setting: definition,
                field: definition,
                format: value => ({ ...definition, value }),
            };
            return { [definition.key]: tag };
        }
        case 'SelectClearable': {
            const tag: InteractiveTitleSettingDefinition<
                SelectClearableDefinition,
                InteractiveTitleSelectClearableDefinition
            > = {
                setting: definition,
                field: definition,
                format: value => ({ ...definition, value }),
            };
            return { [definition.key]: tag };
        }
        case 'SelectNumber': {
            const tag: InteractiveTitleSettingDefinition<
                SelectNumberDefinition,
                InteractiveTitleSelectNumberDefinition
            > = {
                setting: definition,
                field: definition,
                format: value => ({ ...definition, value }),
            };
            return { [definition.key]: tag };
        }
        case 'SelectNumberClearable': {
            const tag: InteractiveTitleSettingDefinition<
                SelectNumberClearableDefinition,
                InteractiveTitleSelectNumberClearableDefinition
            > = {
                setting: definition,
                field: definition,
                format: value => ({ ...definition, value }),
            };
            return { [definition.key]: tag };
        }

        case 'MultiSelect':
        case 'RichMultiSelect': {
            const tag: InteractiveTitleSettingDefinition<
                MultiSelectDefinition | RichMultiSelectDefinition,
                InteractiveTitleMultiSelectDefinition
            > = {
                setting: definition,
                field: {
                    type: 'MultiSelect',
                    value: definition.value,
                    options: definition.options,
                },
                format: value => ({ ...definition, value }),
            };
            return { [definition.key]: tag };
        }

        case 'NumberInput': {
            const tag: InteractiveTitleSettingDefinition<
                NumberInputDefinition,
                InteractiveTitleNumberInputDefinition
            > = {
                setting: definition,
                field: {
                    type: 'Number',
                    value: definition.value,
                    min: definition.min,
                    max: definition.max,
                    step: definition.step,
                },
                format: value => ({ ...definition, value }),
            };
            return { [definition.key]: tag };
        }

        case 'CheckBox': {
            const tag: InteractiveTitleSettingDefinition<
                CheckBoxDefinition,
                InteractiveTitleSelectDefinition
            > = {
                setting: definition,
                field: {
                    type: 'Select',
                    value: String(definition.value),
                    options: [
                        { key: 'true', label: 'true' },
                        { key: 'false', label: 'false' },
                    ],
                },
                format: value => ({ ...definition, value: value === 'true' }),
            };
            return { [definition.key]: tag };
        }

        default:
            return {};
    }
}

function defaultToSignals<T extends SettingFormInputDefinition>(definition: T): Signal[] {
    return [{ name: definition.key, value: definition.value }];
}
